import {
  BrandGroupsListUrlFilters,
  BrandGroupsUrlFiltersEnum,
  BrandsUrlQueryParams
} from "@saleor/brands/url";
import { IFilter, IFilterElement } from "@saleor/components/Filter";
import { commonMessages } from "@saleor/intl";
import { findValueInEnum, maybe, parseBoolean } from "@saleor/misc";
import { FilterOpts } from "@saleor/types";
import {
  BrandGroupInput,
  BrandVerticalEnum,
  ClassificationTypeEnum
} from "@saleor/types/globalTypes";
import {
  createFilterTabUtils,
  createFilterUtils,
  getSingleEnumValueQueryParam,
  getSingleValueQueryParam
} from "@saleor/utils/filters";
import {
  createBooleanField,
  createOptionsField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

export const BRANDGROUPS_FILTERS_KEY = "brandGroupsFilters";

export enum BrandGroupsListFilterKeys {
  vertical = "vertical",
  classification = "classification",
  isSmacAffiliate = "isSmacAffiliate"
}

export interface BrandGroupListFilterOpts {
  // vertical: FilterOpts<BrandVerticalEnum>;
  classification: FilterOpts<ClassificationTypeEnum>;
  isSmacAffiliate: FilterOpts<boolean>;
}

const messages = defineMessages({
  classification: {
    defaultMessage: "Classification",
    description: "brand group type"
  },
  cyberzone: {
    defaultMessage: "Cyberzone",
    description: "vertical type"
  },
  fb: {
    defaultMessage: "Food and Beverages",
    description: "classification"
  },
  gm: {
    defaultMessage: "General Merchandise",
    description: "classification"
  },
  grocery: {
    defaultMessage: "Grocery",
    description: "vertical type"
  },
  isSmacAffiliated: {
    defaultMessage: "Is Smac Affiliated",
    description: "is smac affiliated"
  },
  marketplace: {
    defaultMessage: "Marketplace",
    description: "vertical type"
  },
  retail: {
    defaultMessage: "Retail Affiliates",
    description: "vertical type"
  },
  smFufilled: {
    defaultMessage: "SM Fulfilled",
    description: "vertical type"
  },
  vertical: {
    defaultMessage: "Vertical Type",
    description: "brand group type"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: BrandGroupListFilterOpts
): IFilter<BrandGroupsListFilterKeys> {
  return [
    {
      ...createOptionsField(
        BrandGroupsListFilterKeys.classification,
        intl.formatMessage(messages.classification),
        [opts.classification.value],
        false,
        [
          {
            label: intl.formatMessage(messages.fb),
            value: ClassificationTypeEnum.FOOD_AND_BEVERAGES
          },
          {
            label: intl.formatMessage(messages.gm),
            value: ClassificationTypeEnum.GENERAL_MERCHANDISE
          }
        ]
      ),
      active: opts.classification.active
    },
    {
      ...createBooleanField(
        BrandGroupsListFilterKeys.isSmacAffiliate,
        intl.formatMessage(messages.isSmacAffiliated),
        opts.isSmacAffiliate.value,
        {
          negative: intl.formatMessage(commonMessages.no),
          positive: intl.formatMessage(commonMessages.yes)
        }
      ),
      active: opts.isSmacAffiliate.active
    }
  ];
}

export function getFilterOpts(
  params: BrandGroupsListUrlFilters
): BrandGroupListFilterOpts {
  return {
    classification: {
      active: !!maybe(() => params.classification),
      value: maybe(() =>
        findValueInEnum(params.classification, ClassificationTypeEnum)
      )
    },
    isSmacAffiliate: {
      active: params.isSmacAffiliate !== undefined,
      value: maybe(() => parseBoolean(params.isSmacAffiliate, true))
    }
  };
}

export function getFilterVariables(
  params: BrandGroupsListUrlFilters
): BrandGroupInput {
  return {
    classification: maybe(() =>
      findValueInEnum(params.classification, ClassificationTypeEnum)
    ),
    isSmacAffiliate:
      params.isSmacAffiliate !== undefined
        ? parseBoolean(params.isSmacAffiliate, false)
        : undefined,
    search: params.query,
    vertical: maybe(() => findValueInEnum(params.vertical, BrandVerticalEnum))
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<BrandGroupsListFilterKeys>
): BrandGroupsListUrlFilters {
  const { name } = filter;

  switch (name) {
    case BrandGroupsListFilterKeys.classification:
      return getSingleEnumValueQueryParam(
        filter,
        BrandGroupsUrlFiltersEnum.classification,
        ClassificationTypeEnum
      );

    case BrandGroupsListFilterKeys.vertical:
      return getSingleEnumValueQueryParam(
        filter,
        BrandGroupsUrlFiltersEnum.vertical,
        BrandVerticalEnum
      );
    case BrandGroupsListFilterKeys.isSmacAffiliate:
      return getSingleValueQueryParam(
        filter,
        BrandGroupsUrlFiltersEnum.isSmacAffiliate
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<BrandGroupsListUrlFilters>(BRANDGROUPS_FILTERS_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  BrandGroupsListUrlFilters,
  BrandsUrlQueryParams
>(BrandGroupsUrlFiltersEnum);
